import {
  type ReactNode,
  type ReactElement,
  forwardRef,
  type ComponentPropsWithoutRef,
} from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Error text variants using class-variance-authority
 */
const errorTextVariants = cva('', {
  variants: {
    size: {
      sm: 'text-sm',
      xs: 'text-xs',
    },
    intensity: {
      default: 'text-red-600',
      light: 'text-red-500',
    },
  },
  defaultVariants: {
    size: 'sm',
    intensity: 'default',
  },
});

export interface ErrorTextProps
  extends
    ComponentPropsWithoutRef<'p'>,
    VariantProps<typeof errorTextVariants> {
  /**
   * The text size
   * @default 'sm'
   */
  size?: 'xs' | 'sm';

  /**
   * The color intensity
   * @default 'default'
   */
  intensity?: 'default' | 'light';

  /**
   * The semantic HTML element to render (p, span, div)
   * @default 'p'
   */
  as?: 'p' | 'span' | 'div';

  /**
   * The content to display
   */
  children: ReactNode;
}

/**
 * Base ErrorText component with variant support
 */
const ErrorTextBase = forwardRef<HTMLParagraphElement, ErrorTextProps>(
  (
    {
      size = 'sm',
      intensity = 'default',
      as = 'p',
      children,
      className,
      ...props
    },
    ref
  ): ReactElement => {
    const Component = as;

    return (
      <Component
        ref={ref}
        className={cn(errorTextVariants({ size, intensity }), className)}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

ErrorTextBase.displayName = 'ErrorTextBase';

/**
 * ErrorText - Default error message text
 * @example
 * ```tsx
 * <ErrorText>This field is required</ErrorText>
 * ```
 */
export const ErrorText = forwardRef<
  HTMLParagraphElement,
  Omit<ErrorTextProps, 'size' | 'intensity'>
>(({ as = 'p', ...props }, ref) => (
  <ErrorTextBase ref={ref} size="sm" intensity="default" as={as} {...props} />
));
ErrorText.displayName = 'ErrorText';

/**
 * ErrorTextSmall - Small error message text
 * @example
 * ```tsx
 * <ErrorTextSmall>Validation error</ErrorTextSmall>
 * ```
 */
export const ErrorTextSmall = forwardRef<
  HTMLParagraphElement,
  Omit<ErrorTextProps, 'size' | 'intensity'>
>(({ as = 'p', ...props }, ref) => (
  <ErrorTextBase ref={ref} size="xs" intensity="light" as={as} {...props} />
));
ErrorTextSmall.displayName = 'ErrorTextSmall';
